// downloadUtils.js — pdfmake version
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import { Document, Packer, Paragraph } from "docx";
import { getDescription, getResultDataForLegislation, getTranslatedText } from "./kumuHelper.js";
import { ResultType } from "../../constants.js";
import { stripHtml } from "../../components/ResultCard.js";

// --------------------------------------------------
// HELPERS
// --------------------------------------------------
const getLegislationAttributes = (lg) =>
  lg?.legislation?.value?.attributes || {};

const getLegAtt = (lg) => lg?.value?.attributes || {};

const getAttr = (lg, key, lang = "en") => {
  const attrs = getLegAtt(lg);
  const value =
    lang === "fr"
      ? attrs[`${key} fr`] ?? attrs[key]
      : attrs[key];
  return value || (lang === "fr" ? "Non disponible" : "Not available");
};

const getFormattedDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeText = (text) =>
  String(text || "")
    .normalize("NFKC")
    .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g, " ")
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, "")
    .replace(/â€¯/g, " ")
    .replace(/â‚‚/g, "2")
    .replace(/â€"|â€"/g, "-")
    .replace(/â€˜|â€™/g, "'")
    .replace(/â€œ|â€/g, '"')
    .replace(/\bm3\b/gi, "m³")
    .trim();

export const getStatus = (status) => {
  if (status === ResultType.REQUIRED) return "You may have responsibilities";
  if (status === ResultType.LEARN) return "Learn More";
  return status;
};

const getAnswer = (result, lang) => {
  if (!result)
    return lang === "fr"
      ? "Non disponible: questions non répondues"
      : "Not available, questions not answered";
  if (result === "Required")
    return lang === "fr"
      ? "Vous pourriez avoir des responsabilités"
      : "You may have responsibilities";
  if (result === "Learn")
    return lang === "fr"
      ? "Vous n'avez probablement pas de responsabilités"
      : "You likely do not have responsibilities";
  return lang === "fr"
    ? "Non disponible: questions non répondues"
    : "Not available, questions not answered";
};

// --------------------------------------------------
// LEGISLATION NAME ITALICIZATION
// --------------------------------------------------
const legislationNamePattern =
  /([A-ZÀ-ÖØ-Ý][A-Za-zÀ-ÖØ-öø-ÿ0-9,'()\-]*(?:\s+(?:[A-ZÀ-ÖØ-Ý][A-Za-zÀ-ÖØ-öø-ÿ0-9,'()\-]*|and|of|the|for|de|du|des|et|la|le|d'))*\s+(?:Regulations|Regulation|Act|Order|Code|Loi|Règlement|Reglement|Décret))/g;

const applyLegislationItalics = (text, baseBold = false) => {
  if (!text) return [];

  const hasLeadingSpace = /^\s/.test(text);
  const hasTrailingSpace = /\s$/.test(text);

  const normalized = normalizeText(text);
  if (!normalized) {
    if (hasLeadingSpace || hasTrailingSpace) return [{ text: " ", bold: baseBold }];
    return [];
  }

  const segments = [];
  let lastIndex = 0;
  legislationNamePattern.lastIndex = 0;

  let match = legislationNamePattern.exec(normalized);
  while (match) {
    const start = match.index;
    let end = start + match[0].length;
    let matchedText = match[0];

    if (/^Constitution\s+Act$/i.test(matchedText)) {
      const suffixMatch = normalized.slice(end).match(/^\s*,\s*1982\b/);
      if (suffixMatch) {
        matchedText += suffixMatch[0];
        end += suffixMatch[0].length;
      }
    }

    if (start > lastIndex) {
      segments.push({
        text: normalized.slice(lastIndex, start),
        bold: baseBold,
      });
    }

    const shouldKeepNormal =
      /\bthe\s+Regulations[\s.,;:!?)]*$/i.test(matchedText) &&
      !/^Transportation\s+of\s+Dangerous\s+Goods\s+Regulations/i.test(matchedText);

    segments.push({
      text: matchedText,
      italics: !shouldKeepNormal,
      bold: baseBold,
    });

    lastIndex = end;
    match = legislationNamePattern.exec(normalized);
  }

  if (lastIndex < normalized.length) {
    segments.push({
      text: normalized.slice(lastIndex),
      bold: baseBold,
    });
  }

  if (!segments.length) {
    segments.push({ text: normalized, bold: baseBold });
  }

  // Restore boundary spaces that normalizeText trimmed
  if (hasLeadingSpace && segments.length > 0 && !segments[0].text.startsWith(" ")) {
    segments[0] = { ...segments[0], text: " " + segments[0].text };
  }
  if (hasTrailingSpace && segments.length > 0 && !segments[segments.length - 1].text.endsWith(" ")) {
    const last = segments.length - 1;
    segments[last] = { ...segments[last], text: segments[last].text + " " };
  }

  return segments;
};

// --------------------------------------------------
// SEPARATOR LINE
// --------------------------------------------------
const horizontalRule = (pageContentWidth) => ({
  canvas: [
    {
      type: "line",
      x1: 0,
      y1: 0,
      x2: pageContentWidth,
      y2: 0,
      lineWidth: 0.75,
      lineColor: "#bebebe",
    },
  ],
  margin: [0, 8, 0, 8],
});

// --------------------------------------------------
// HTML → PDFMAKE CONTENT CONVERTER
// --------------------------------------------------
const htmlToPdfmakeContent = (htmlString, baseBold = false) => {
  if (!htmlString) return [];

  const parser = new DOMParser();
  const normalized = String(htmlString)
    .replace(
      /<i>\s*Constitution\s+Act\s*<\/i>\s*,\s*1982/gi,
      "<i>Constitution Act, 1982</i>"
    );
  const parsed = parser.parseFromString(normalized, "text/html");
  const content = [];

  const inlineTags = new Set([
    "a", "i", "em", "strong", "b", "span", "sup", "sub",
    "abbr", "cite", "code", "small", "u",
  ]);

  const isInlineNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return true;
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    const tag = node.tagName.toLowerCase();
    return inlineTags.has(tag) || tag === "br";
  };

    const walkInline = (node, activeUrl = null, bold = baseBold, italic = false) => {
    if (!node) return [];

    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.nodeValue;
      if (!raw) return [];

      const hasLeadingSpace = /^\s/.test(raw);
      const hasTrailingSpace = /\s$/.test(raw);

      let text = normalizeText(raw);
      if (!text) {
        if (hasLeadingSpace || hasTrailingSpace) return [{ text: " " }];
        return [];
      }

      if (hasLeadingSpace && !text.startsWith(" ")) text = " " + text;
      if (hasTrailingSpace && !text.endsWith(" ")) text = text + " ";

      const legRuns = applyLegislationItalics(text, bold);
      return legRuns.map((run) => ({
        text: run.text,
        bold: run.bold || false,
        italics: run.italics || italic,
        ...(activeUrl
          ? { link: activeUrl, color: "#0000EE", decoration: "underline" }
          : {}),
      }));
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return [];

    const tag = node.tagName.toLowerCase();

    if (tag === "br") return [{ text: "\n" }];

    const nextBold = tag === "strong" || tag === "b" ? true : bold;
    const nextItalic = tag === "i" || tag === "em" ? true : italic;

    // Check siblings for missing spaces around ANY inline element
    const prevSibText = node.previousSibling?.nodeValue
      || node.previousSibling?.textContent || "";
    const nextSibText = node.nextSibling?.nodeValue
      || node.nextSibling?.textContent || "";
    const needsLeadingSpace =
      prevSibText.length > 0 && !/\s$/.test(prevSibText);
    const needsTrailingSpace =
      nextSibText.length > 0
      && !/^\s/.test(nextSibText)
      && !/^[.,;:!?)]\s*/.test(nextSibText);

    const segments = [];
    if (needsLeadingSpace) segments.push({ text: " " });

    if (tag === "sup") {
      const children = Array.from(node.childNodes).flatMap((child) =>
        walkInline(child, activeUrl, nextBold, nextItalic)
      );
      segments.push(
        ...children.map((seg) => ({ ...seg, fontSize: 7, sup: true }))
      );
    } else if (tag === "a") {
      const href = node.getAttribute("href") || activeUrl;
      segments.push(
        ...Array.from(node.childNodes).flatMap((child) =>
          walkInline(child, href, nextBold, nextItalic)
        )
      );
    } else {
      segments.push(
        ...Array.from(node.childNodes).flatMap((child) =>
          walkInline(child, activeUrl, nextBold, nextItalic)
        )
      );
    }

    if (needsTrailingSpace) segments.push({ text: " " });
    return segments;
  };

  const blockTags = new Set([
    "p", "div", "section", "article",
    "h1", "h2", "h3", "h4", "h5", "h6",
  ]);

  const flushInlineBuffer = (buffer) => {
    if (!buffer.length) return;

    const segments = buffer.flatMap((child) => walkInline(child));
    const hasVisible = segments.some(
      (s) => s.text && s.text.trim().length > 0
    );

    if (hasVisible) {
      const cleaned = cleanSegments(segments);
      if (cleaned.length) {
        content.push({ text: cleaned, margin: [0, 0, 0, 4] });
      }
    }
  };

  const cleanSegments = (segments) => {
    const result = [];
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (seg.text === "\n") {
        const remaining = segments.slice(i + 1);
        const hasMoreVisible = remaining.some(
          (s) => s.text && s.text.replace(/\n/g, "").trim().length > 0
        );
        if (hasMoreVisible) {
          result.push(seg);
        }
        continue;
      }
      if (seg.text && /^\s*[.,:;]\s*$/.test(seg.text) && result.length > 0) {
        const last = result[result.length - 1];
        const punct = seg.text.trim();
        if (last.text && !last.text.endsWith(punct)) {
          result[result.length - 1] = { ...last, text: last.text + punct };
        }
        continue;
      }
      result.push(seg);
    }
    return result;
  };

  const processChildren = (parentNode) => {
    let inlineBuffer = [];

    Array.from(parentNode.childNodes).forEach((child) => {
      if (isInlineNode(child)) {
        inlineBuffer.push(child);
        return;
      }

      flushInlineBuffer(inlineBuffer);
      inlineBuffer = [];

      if (child.nodeType !== Node.ELEMENT_NODE) return;

      const tag = child.tagName.toLowerCase();

      if (tag === "ul") {
        const items = [];
        Array.from(child.childNodes).forEach((liNode) => {
          if (
            liNode.nodeType === Node.ELEMENT_NODE &&
            liNode.tagName.toLowerCase() === "li"
          ) {
            const segments = Array.from(liNode.childNodes).flatMap((c) =>
              walkInline(c)
            );
            const cleaned = cleanSegments(segments);
            items.push({
              text: cleaned.length ? cleaned : [{ text: "" }],
            });
          }
        });
        if (items.length) {
          content.push({
            ul: items,
            margin: [12, 2, 0, 4],
            style: "listItem",
          });
        }
        return;
      }

      if (tag === "ol") {
        const items = [];
        Array.from(child.childNodes).forEach((liNode) => {
          if (
            liNode.nodeType === Node.ELEMENT_NODE &&
            liNode.tagName.toLowerCase() === "li"
          ) {
            const segments = Array.from(liNode.childNodes).flatMap((c) =>
              walkInline(c)
            );
            const cleaned = cleanSegments(segments);
            items.push({
              text: cleaned.length ? cleaned : [{ text: "" }],
            });
          }
        });
        if (items.length) {
          content.push({
            ol: items,
            margin: [12, 2, 0, 4],
            style: "listItem",
          });
        }
        return;
      }

      if (blockTags.has(tag)) {
        const segments = Array.from(child.childNodes).flatMap((c) =>
          walkInline(c)
        );
        const hasVisible = segments.some(
          (s) => s.text && s.text.trim().length > 0
        );
        if (hasVisible) {
          const cleaned = cleanSegments(segments);
          if (cleaned.length) {
            content.push({ text: cleaned, margin: [0, 0, 0, 4] });
          }
        }
        return;
      }

      processChildren(child);
    });

    flushInlineBuffer(inlineBuffer);
    inlineBuffer = [];
  };

  processChildren(parsed.body);
  return content;
};

// --------------------------------------------------
// DISCLAIMER TEXT
// --------------------------------------------------
const disclaimerEn = `
  <section>
    <p>The Mine Permit Navigator is for information purposes only. It is not a substitute for any federal legislation and regulations. The federal legislation and regulations prevail in the event of any inconsistency with this tool. In case of any doubt, do not hesitate to reach out to the responsible federal authority, which can be found in the <a href="https://mpn-tool-outil-npm.nrcan-rncan.cloud-nuage.canada.ca/en/faq">Frequently Asked Questions</a>.</p>
    <p>The Mine Permit Navigator includes federal legislation only, excluding specific requirements for projects north of the 60<sup>th</sup> parallel. It is recommended that you review legislation specific to working north of the 60<sup>th</sup> parallel. It is also recommended that you review provincial and territorial legislation, as your project may be subject to additional requirements at the provincial and territorial level.</p>
    <p>The Mine Permit Navigator does not include any information regarding the duty to consult with Indigenous Peoples in Canada. The federal government has a legal duty to consult when it contemplates conduct that will create adverse impacts to Indigenous and Treaty rights. A duty to consult could arise in the context of regulatory project approvals, permit, licensing and authorization, operational choices, policy creation, negotiations, and other related actions. Furthermore, statutory requirements, provisions in land claim agreements, and consultation processes may all create the duty to consult. Proponents are responsible for engaging with the relevant federal departments that oversee any licences or permits they seek, to ensure they are fully informed about any potential duty to consult and can plan early engagement and consultation processes accordingly.</p>
    <p>It is encouraged that early and meaningful engagement take place to ensure that potentially impacted Indigenous groups and other parties are informed of potential projects, actions or regulatory permits, to build relationships, and to understand and respond to any concerns an Indigenous group or other groups may have. If a formal duty to consult exists, the Crown consultation process will be undertaken to ensure an understanding of how the project or issuance of a permit may impact Indigenous rights and interests, and whether these impacts can be avoided, mitigated, or, where appropriate, accommodated in order to fully inform a decision on the proposed project or required licence or permit. Meaningful and sufficient consultation can contribute to reconciliation by fostering relationships with Indigenous Peoples, creating the basis for future work together.</p>
  </section>
`;

const disclaimerFr = `
  <section>
    <p>Le Navigateur de permis miniers est fourni à titre informatif uniquement. Il ne remplace en aucun cas les lois et règlements fédéraux. En cas de divergence entre cet outil et la législation et les règlements fédéraux, ce sont ces derniers qui prévaudront. En cas de doute, n'hésitez pas à contacter les autorités fédérales responsables, indiquées dans la section <a href="https://mpn-tool-outil-npm.nrcan-rncan.cloud-nuage.canada.ca/fr/faq">questions fréquemment posées</a>.</p>
    <p>Le Navigateur de permis miniers ne comprend que la législation fédérale, à l'exclusion des exigences spécifiques pour les projets au nord du 60e parallèle. Il est recommandé d'examiner les lois spécifiques applicables au travail au nord du 60e parallèle. Il est également recommandé de consulter la législation provinciale et territoriale, car votre projet pourrait être soumis à des exigences supplémentaires au niveau provincial et territorial.</p>
    <p>Le Navigateur de permis miniers ne fournit aucune information concernant l'obligation de consulter les peuples autochtones au Canada. Le gouvernement fédéral a l'obligation légale de consulter lorsqu'il envisage des mesures susceptibles d'avoir des effets négatifs sur les droits ancestraux ou issus de traités des peuples autochtones. L'obligation de consulter peut notamment s'appliquer dans le cadre de l'approbation de projets réglementaires, de la délivrance de permis, de licences ou d'autorisations, de choix opérationnels, de l'élaboration de politiques, de négociations et d'autres mesures connexes. De plus, les règlements sur les textes réglementaires, des dispositions prévues dans des accords sur des revendications territoriales et des processus de consultation peuvent également donner lieu à une obligation de consulter. Les promoteurs sont responsables d'entrer en communication avec les ministères fédéraux concernés qui sont responsables des licences ou permis demandés, afin de s'assurer qu'ils sont pleinement informés de toute obligation éventuelle de consulter et qu'ils puissent planifier en conséquence des activités de mobilisation et de consultation précoce.</p>
    <p>Il est recommandé d'engager tôt un dialogue significatif afin que les groupes autochtones potentiellement touchés et d'autres parties soient informés des projets, mesures ou permis réglementaires envisagés, de favoriser des relations de confiance et de répondre aux préoccupations soulevées. Si une obligation officielle de consultation existe, le processus de consultation de la Couronne sera entrepris pour évaluer les répercussions du projet ou du permis sur les droits et intérêts autochtones, et déterminer comment éviter, atténuer ou, le cas échéant, compenser ces répercussions. Une consultation suffisante et significative peut contribuer à la réconciliation en renforçant les relations avec les peuples autochtones et en jetant les bases d'une collaboration future.</p>
  </section>
`;

// --------------------------------------------------
// NOTICE TEXT
// --------------------------------------------------
const noticeTextEn =
  "Please note: the content of this PDF is displayed in the language of the original submission and has not been altered.";
const noticeTextFr =
  "Veuillez noter : le contenu de ce PDF est affiché dans la langue de la demande originale et n'a pas été modifié.";

// --------------------------------------------------
// LOAD LOGO AS BASE64
// --------------------------------------------------
const loadImageAsBase64 = async (src) => {
  const response = await fetch(src);
  if (!response.ok) throw new Error(`Unable to fetch image: ${src}`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// --------------------------------------------------
// BUILD A SINGLE LEGISLATION RESULT CARD
// --------------------------------------------------
const buildResultCard = (lg, language, pageContentWidth, isFirst = false) => {
  const isFr = language === "fr";
  const result = getResultDataForLegislation(lg.name, language);
  const explanation = result ? getDescription(result, language) : "";
  const reasonWithExplanation = [lg.reason, explanation]
    .filter(Boolean)
    .join("<br/>");

  const sections = isFr
    ? [
        ["Résultat(s)", reasonWithExplanation || "Non disponible"],
        ["Informations de contact", getAttr(result, "contact", "fr")],
        ["Comment postuler ou signaler", getAttr(result, "where to apply", "fr")],
        ["Guide d'application et de réglementation", getAttr(result, "guidance", "fr")],
        ["Échéanciers", getAttr(result, "timelines", "fr")],
        ["Exemples de demandes", getAttr(result, "examples", "fr")],
        ["Site Web du programme", getAttr(result, "program website", "fr")],
        ["Législation géographique", getAttr(result, "geographical legislation", "fr")],
      ]
    : [
        ["Result(s)", reasonWithExplanation || "Not available"],
        ["Contact Information", getAttr(result, "contact")],
        ["How to Apply or Report", getAttr(result, "where to apply")],
        ["Application and Regulatory Guidance", getAttr(result, "guidance")],
        ["Timelines", getAttr(result, "timelines")],
        ["Application Examples", getAttr(result, "examples")],
        ["Program Website", getAttr(result, "program website")],
        ["Geographical Legislation", getAttr(result, "geographical legislation")],
      ];

  const cardContent = [];

  if (!isFirst) {
    cardContent.push(horizontalRule(pageContentWidth));
  }

  cardContent.push({
    text: applyLegislationItalics(
      getTranslatedText(language, lg.name),
      true
    ),
    style: "legislationTitle",
    margin: [0, 4, 0, 4],
  });

  cardContent.push({
    text: getAnswer(lg.status, language),
    bold: true,
    color: "#d3080c",
    fontSize: 11,
    margin: [0, 0, 0, 8],
  });

  sections.forEach(([label, value]) => {
    cardContent.push({
      text: label,
      bold: true,
      fontSize: 11,
      margin: [0, 4, 0, 2],
    });

    const htmlContent = htmlToPdfmakeContent(
      value || (isFr ? "Non disponible" : "Not available")
    );

    if (htmlContent.length) {
      htmlContent.forEach((block) => {
        cardContent.push({ ...block, fontSize: 10 });
      });
    } else {
      cardContent.push({
        text: normalizeText(
          stripHtml(value || (isFr ? "Non disponible" : "Not available"))
        ),
        fontSize: 10,
        margin: [0, 0, 0, 4],
      });
    }
  });

  const answersLabel = isFr
    ? "Vos réponses aux questions de la recherche guidée"
    : "Your Answers to the Guided Search Questions";

  cardContent.push({
    text: answersLabel,
    bold: true,
    fontSize: 11,
    margin: [0, 4, 0, 2],
  });

  const history =
    Array.isArray(lg.history) && lg.history.length
      ? lg.history
      : [isFr ? "Aucune question" : "No questions"];

  const historyItems = history.map((question) => {
    const text = stripHtml(question)
      .replace(/\s*\n\s*;\s*/g, "; ")
      .replace(/\s*\n\s*/g, " ");
    return { text: normalizeText(text) };
  });

  cardContent.push({
    ol: historyItems,
    fontSize: 10,
    margin: [12, 2, 0, 4],
  });

  return {
    stack: cardContent,
    margin: [0, 0, 0, 10],
  };
};

// --------------------------------------------------
// MAIN EXPORT
// --------------------------------------------------
export const downloadResultAsPdfOrDocx = async (
  dataArray,
  type = "pdf",
  language = "en"
) => {
  console.log(dataArray)
  console.log("downloadUtils pdfmake - items:", dataArray?.length);

  const isFr = language === "fr";
  const formattedDate = getFormattedDate();
  const filename = isFr
    ? `Navigateur_de_permis_miniers_Resultats_${formattedDate}`
    : `Mine_Permit_Navigator_Results_${formattedDate}`;

  if (type === "pdf") {
    const pageMargin = 54;
    const pageContentWidth = 612 - pageMargin * 2;

    let logoBase64 = null;
    try {
      logoBase64 = await loadImageAsBase64(
        "/static/graphics/Natural_Resources_Canada.png"
      );
    } catch (error) {
      console.warn("PDF logo could not be loaded", error);
    }

    const content = [];

    if (logoBase64) {
      content.push({
        image: "logo",
        width: 320,
        margin: [0, 0, 0, 20],
      });
    }

    content.push({
      text: isFr ? "Navigateur de permis miniers" : "Mine Permit Navigator",
      style: "mainTitle",
    });

    content.push({
      table: {
        widths: ["*"],
        body: [
          [
            {
              text: [
                { text: isFr ? "Veuillez noter : " : "Please note: ", bold: true },
                {
                  text: isFr
                    ? "le contenu de ce PDF est affiché dans la langue de la demande originale et n'a pas été modifié."
                    : "the content of this PDF is displayed in the language of the original submission and has not been altered.",
                },
              ],
              fontSize: 10,
              margin: [6, 6, 6, 6],
            },
          ],
        ],
      },
      layout: {
        fillColor: () => "#e6f9ff",
        hLineColor: () => "#99d6eb",
        vLineColor: () => "#99d6eb",
        hLineWidth: () => 1,
        vLineWidth: () => 1,
      },
      margin: [0, 0, 0, 16],
    });

    content.push({
      text: isFr ? "Vos résultats" : "Your Results",
      style: "sectionTitle",
    });

    dataArray.forEach((lg, index) => {
      content.push(buildResultCard(lg, language, pageContentWidth, index === 0));
    });

    content.push(horizontalRule(pageContentWidth));

    content.push({
      text: isFr ? "Avis de non-responsabilité" : "Disclaimer",
      style: "sectionTitle",
      margin: [0, 8, 0, 6],
    });

    const disclaimerContent = htmlToPdfmakeContent(
      isFr ? disclaimerFr : disclaimerEn
    );
    disclaimerContent.forEach((block) => {
      content.push({ ...block, fontSize: 10 });
    });

    const docDefinition = {
      pageSize: "LETTER",
      pageMargins: [pageMargin, pageMargin, pageMargin, pageMargin],
      content,
      images: logoBase64 ? { logo: logoBase64 } : {},
      styles: {
        mainTitle: {
          fontSize: 18,
          bold: true,
          margin: [0, 0, 0, 12],
        },
        sectionTitle: {
          fontSize: 16,
          bold: true,
          margin: [0, 0, 0, 10],
        },
        legislationTitle: {
          fontSize: 13,
          bold: true,
          italics: true,
        },
        listItem: {
          fontSize: 10,
        },
      },
      defaultStyle: {
        font: "Roboto",
        fontSize: 11,
        lineHeight: 1.3,
      },
    };

    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes("Mac") && navigator.maxTouchPoints > 1);

    if (isIOS) {
      pdfMake.createPdf(docDefinition).open();
    } else {
      pdfMake.createPdf(docDefinition).download(`${filename}.pdf`);
    }
  }

  if (type === "docx") {
    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              text: isFr ? "Vos résultats" : "Your Results",
              heading: "Heading1",
            }),
          ],
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.docx`;
    link.click();
  }
};